/**
 * Parser del JSON público de Shopify (products.json) → tipo Product de la app.
 *
 * El catálogo de elixirperfumes.es no tiene tags olfativos ni notas estructuradas.
 * Este módulo extrae:
 *   - marca real desde el TITLE (ej: "SALVO INTENSE Maison alhambra perfume Hombre 100ml" → "Maison Alhambra")
 *   - tamaño en ml desde el TITLE (… 100ml)
 *   - género desde tags + product_type + title
 *   - familia olfativa, notas, concentración, intensidad desde la descripción HTML
 *
 * Se queda en 80% bien al 1er intento; mejorable iterando con datos reales.
 */

import type { Concentration, Gender, OlfactoryFamily, Product } from '@/types';

// ── Marcas conocidas en el catálogo Elixir (ordenadas por prioridad de match — las
//    multi-palabra van primero para evitar que "Arabiyat" gane antes que "Arabiyat Prestige")
const KNOWN_BRANDS = [
  'Aphorismes de Dominique Ropion',
  'Reine de Saba',
  'Al Jazeera Perfumes',
  'Al Jazeera',
  'Maison Alhambra',
  'Arabiyat Prestige',
  'Arabiyat',
  'Paris Corner',
  'French Avenue',
  'Frecnh avenue',     // typo recurrente en datos reales (sic)
  'Swiss Arabian',
  'Al Haramain',
  'Al Wataniah',
  'Aromatix',
  'Bharara',
  'Orientica',
  'Lattafa',
  'Lataffa',           // typo recurrente — se normaliza a Lattafa
  'Laverne',
  'Fragance world',    // typo recurrente — se normaliza a Fragrance World
  'Fragrance World',
  'Riiffs',
  'Dumont',
  'Rasasi',
  'Rayhaan',
  'Asdaaf',
  'Afnan',
  'Armaf',
  'Ayat',
];

// Normalización de marcas: si el title trae el typo, devolvemos la marca correcta.
const BRAND_NORMALIZE: Record<string, string> = {
  'Frecnh avenue': 'French Avenue',
  'Lataffa': 'Lattafa',
  'Fragance world': 'Fragrance World',
};

// ── Marcas que el dueño distribuye en exclusiva (peso especial en matching)
const EXCLUSIVE_BRANDS = new Set(
  ['Aphorismes de Dominique Ropion', 'Al Jazeera Perfumes', 'Al Jazeera', 'Reine de Saba'].map((b) =>
    b.toLowerCase()
  )
);

// ── Notas olfativas: forma canónica → sinónimos a buscar en la descripción.
// La forma canónica es lo que mostramos en la UI; los sinónimos son ES+EN+acentos.
const NOTE_SYNONYMS: Record<string, string[]> = {
  bergamota: ['bergamota', 'bergamot'],
  limón: ['limón', 'limon', 'lemon'],
  naranja: ['naranja', 'orange'],
  mandarina: ['mandarina', 'mandarin'],
  pomelo: ['pomelo', 'grapefruit'],
  lima: ['lima', 'lime'],
  rosa: ['rosa ', 'rose '],            // espacio final para evitar "rosado", "rosario"
  jazmín: ['jazmín', 'jazmin', 'jasmine'],
  azahar: ['azahar', 'orange blossom', 'flor de naranjo'],
  iris: ['iris'],
  tuberosa: ['tuberosa', 'tuberose'],
  lirio: ['lirio'],
  gardenia: ['gardenia'],
  magnolia: ['magnolia'],
  sándalo: ['sándalo', 'sandalo', 'sandalwood'],
  cedro: ['cedro', 'cedar'],
  vetiver: ['vetiver'],
  pachuli: ['pachuli', 'patchouli'],
  oud: ['oud', 'agarwood', 'oudh'],
  incienso: ['incienso', 'frankincense'],
  mirra: ['mirra', 'myrrh'],
  benjuí: ['benjuí', 'benzoin'],
  ámbar: ['ámbar', 'ambar', 'amber'],
  almizcle: ['almizcle', 'musk'],
  vainilla: ['vainilla', 'vanilla'],
  caramelo: ['caramelo', 'caramel'],
  praliné: ['praliné', 'praline'],
  miel: ['miel ', 'honey'],
  tonka: ['tonka'],
  café: ['café', 'coffee'],
  cacao: ['cacao', 'cocoa'],
  chocolate: ['chocolate'],
  canela: ['canela', 'cinnamon'],
  cardamomo: ['cardamomo', 'cardamom'],
  pimienta: ['pimienta', 'pepper'],
  azafrán: ['azafrán', 'azafran', 'saffron'],
  jengibre: ['jengibre', 'ginger'],
  clavo: ['clavo de olor'],
  'nuez moscada': ['nuez moscada', 'nutmeg'],
  manzana: ['manzana', 'apple'],
  pera: ['pera ', 'pear'],
  fresa: ['fresa', 'strawberry'],
  frambuesa: ['frambuesa', 'raspberry'],
  mora: ['mora ', 'blackberry'],
  cereza: ['cereza', 'cherry'],
  piña: ['piña', 'pineapple'],
  coco: ['coco ', 'coconut'],
  mango: ['mango'],
  maracuyá: ['maracuyá', 'maracuya', 'passion fruit'],
  lychee: ['lychee', 'lichi'],
  higo: ['higo', 'fig '],
  lavanda: ['lavanda', 'lavender'],
  menta: ['menta ', 'mint'],
  romero: ['romero', 'rosemary'],
  salvia: ['salvia', 'sage'],
  tomillo: ['tomillo', 'thyme'],
  cuero: ['cuero', 'leather'],
  tabaco: ['tabaco', 'tobacco'],
  whisky: ['whisky', 'whiskey'],
  ron: [' ron ', ' rum '],
  algas: ['algas', 'algae', 'seaweed'],
  marina: ['notas marinas', 'marine'],
  ozónico: ['ozónico', 'ozonico'],
};

// ── Mapeo de palabras clave de descripción → familia olfativa
const FAMILY_KEYWORDS: Record<OlfactoryFamily, string[]> = {
  oud: ['oud', 'agarwood', 'oudh'],
  oriental: ['oriental', 'orientales'],
  ambar: ['ámbar', 'ambar', 'amber', 'ambarado', 'ambarada'],
  amaderado: ['amaderad', 'maderada', 'maderoso', 'wood', 'maderas'],
  floral: ['floral', 'florales'],
  citrico: ['cítric', 'citric', 'hesperid'],
  fresco: ['fresc', 'aromática fresca', 'aromat'],
  dulce: ['gourmand', 'dulce', 'azucarad'],
  afrutado: ['afrutad', 'frutal', 'fruity'],
};

const FAMILY_PRIORITY: OlfactoryFamily[] = [
  'oud', 'oriental', 'ambar', 'amaderado', 'dulce', 'afrutado', 'floral', 'citrico', 'fresco',
];

// ─────────────────────────────────────────────────────────────
// Tipos del JSON crudo de Shopify
// ─────────────────────────────────────────────────────────────
export interface ShopifyVariantRaw {
  id: number;
  title: string;
  price: string;
  sku: string | null;
  available: boolean;
  option1?: string;
}

export interface ShopifyImageRaw {
  src: string;
  alt: string | null;
  position: number;
}

export interface ShopifyProductRaw {
  id: number;
  title: string;
  handle: string;
  body_html: string;
  vendor: string;
  product_type: string;
  tags: string[] | string;
  variants: ShopifyVariantRaw[];
  images: ShopifyImageRaw[];
  published_at?: string | null;
  updated_at?: string;
}

// ─────────────────────────────────────────────────────────────
// Helpers de extracción
// ─────────────────────────────────────────────────────────────
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, ''); // quita acentos
}

function parseBrandAndName(title: string): { brand: string | undefined; name: string; sizeMl?: number } {
  // Patrón típico: "SALVO INTENSE Maison alhambra perfume Hombre 100ml"
  const sizeMatch = title.match(/(\d{2,4})\s*ml\b/i);
  const sizeMl = sizeMatch ? parseInt(sizeMatch[1], 10) : undefined;

  // Buscar la marca conocida en el title
  const titleLow = normalize(title);
  for (const brand of KNOWN_BRANDS) {
    if (titleLow.includes(normalize(brand))) {
      const idx = titleLow.indexOf(normalize(brand));
      const namePart = title.slice(0, idx).trim();
      const canonical = BRAND_NORMALIZE[brand] ?? brand;
      return { brand: canonical, name: namePart || title, sizeMl };
    }
  }

  // No hemos encontrado marca conocida → asumimos primer ~3 palabras = nombre, resto se descarta
  const cleanedTitle = title.replace(/\s*perfume\s+(hombre|mujer|unisex)\s*\d*ml?$/i, '').trim();
  return { brand: undefined, name: cleanedTitle, sizeMl };
}

function parseGender(title: string, tags: string[] | string, productType: string): Gender | undefined {
  const tagArr = Array.isArray(tags) ? tags.map(normalize) : normalize(String(tags)).split(/[,\s]+/);
  const hasH = tagArr.includes('hombre') || /perfume\s+hombre/i.test(title) || /HOMBRE/.test(productType);
  const hasM = tagArr.includes('mujer') || /perfume\s+mujer/i.test(title) || /MUJER/.test(productType);
  if (/unisex/i.test(title)) return 'unisex';
  if (hasH && hasM) return 'unisex';
  if (hasH) return 'hombre';
  if (hasM) return 'mujer';
  return undefined;
}

function inferFamily(text: string): OlfactoryFamily | undefined {
  const lower = normalize(text);
  // Búsqueda por prioridad: si contiene oud → oud, etc.
  for (const fam of FAMILY_PRIORITY) {
    const kws = FAMILY_KEYWORDS[fam];
    if (kws.some((kw) => lower.includes(normalize(kw)))) return fam;
  }
  return undefined;
}

function extractNotes(text: string): string[] {
  // Pongo espacios alrededor para que los matches con espacio (ej " rum ") funcionen
  const lower = ` ${normalize(text)} `;
  const found: string[] = [];
  for (const [canonical, syns] of Object.entries(NOTE_SYNONYMS)) {
    if (syns.some((s) => lower.includes(normalize(s)))) found.push(canonical);
  }
  return found;
}

function inferConcentration(text: string): Concentration | undefined {
  const lower = normalize(text);
  if (/\bextrait\b|\bextracto\b/.test(lower)) return 'Extrait';
  if (/\bparfum\b|\bperfume\s+puro\b/.test(lower)) return 'Parfum';
  if (/\beau\s+de\s+parfum\b|\bedp\b/.test(lower)) return 'EDP';
  if (/\beau\s+de\s+toilette\b|\bedt\b/.test(lower)) return 'EDT';
  return undefined;
}

function inferIntensity(text: string): 1 | 2 | 3 | 4 | 5 | undefined {
  const lower = normalize(text);
  if (/\bextrait\b|\boud\b|muy intens/.test(lower)) return 5;
  if (/\bintens|\bparfum\b|profund/.test(lower)) return 4;
  if (/\bedp\b|envolvent|magnetic/.test(lower)) return 3;
  if (/\bedt\b|fresc|ligero|luminos/.test(lower)) return 2;
  return undefined;
}

function inferProjection(text: string): 1 | 2 | 3 | 4 | 5 | undefined {
  const lower = normalize(text);
  if (/estela poderos|gran proyecci|proyect.*alta/.test(lower)) return 5;
  if (/proyect|estela/.test(lower)) return 4;
  if (/intim|cercan|piel/.test(lower)) return 2;
  return 3;
}

// ─────────────────────────────────────────────────────────────
// API pública
// ─────────────────────────────────────────────────────────────
export interface ParseOptions {
  /** Base URL pública de la tienda — necesaria para construir product_url. Ej: https://elixirperfumes.es */
  storeBaseUrl?: string;
}

export function parseShopifyProduct(raw: ShopifyProductRaw, opts: ParseOptions = {}): Product {
  const desc = stripHtml(raw.body_html ?? '');
  const fullText = `${raw.title} ${desc}`;

  const { brand, sizeMl } = parseBrandAndName(raw.title);
  const gender = parseGender(raw.title, raw.tags, raw.product_type);
  const family = inferFamily(fullText);
  const allNotes = extractNotes(desc);

  // Heurística básica: las primeras notas mencionadas → top, intermedias → heart, últimas → base.
  // No es preciso pero da una pirámide razonable cuando la web no estructura.
  const noteSlice = (start: number, end: number) =>
    allNotes.slice(start, Math.max(end, start + 1));
  const third = Math.ceil(allNotes.length / 3);
  const notesTop = noteSlice(0, third);
  const notesHeart = noteSlice(third, third * 2);
  const notesBase = noteSlice(third * 2, allNotes.length);

  const concentration = inferConcentration(fullText);
  const intensity = inferIntensity(desc);
  const projection = inferProjection(desc);

  const inStock = raw.variants.some((v) => v.available);
  const minPrice = raw.variants
    .map((v) => parseFloat(v.price))
    .filter((p) => !Number.isNaN(p))
    .reduce((min, p) => (p < min ? p : min), Number.POSITIVE_INFINITY);

  const productUrl = opts.storeBaseUrl
    ? `${opts.storeBaseUrl.replace(/\/$/, '')}/products/${raw.handle}`
    : undefined;

  const tagsArr = Array.isArray(raw.tags)
    ? raw.tags
    : typeof raw.tags === 'string'
    ? raw.tags.split(',').map((t) => t.trim()).filter(Boolean)
    : [];

  return {
    id: String(raw.id),
    handle: raw.handle,
    title: raw.title,
    brand,
    vendor: raw.vendor,
    family,
    notesTop,
    notesHeart,
    notesBase,
    concentration,
    gender,
    intensity,
    projection,
    price: Number.isFinite(minPrice) ? minPrice : undefined,
    currency: 'EUR',
    imageUrl: raw.images[0]?.src,
    description: desc.slice(0, 800),
    productUrl,
    tags: tagsArr,
    isExclusive: brand ? EXCLUSIVE_BRANDS.has(brand.toLowerCase()) : false,
    inStock,
    variants: raw.variants.map((v) => ({
      id: String(v.id),
      sku: v.sku ?? undefined,
      sizeMl,
      price: parseFloat(v.price),
      inStock: v.available,
    })),
  };
}

export function parseShopifyCatalog(
  raw: { products: ShopifyProductRaw[] },
  opts: ParseOptions = {}
): Product[] {
  return raw.products.map((p) => parseShopifyProduct(p, opts));
}
